import { gsap } from 'gsap';
import { containerNames } from '../../constants';
import { PixiBase } from '../../core/pixi/PixiBase';
import { Spine } from '../../services/elementTypeService/Spine';
import { isoStandard } from '../../helpers/isoStandard';
import { gameService, soundService } from '../../services';
import { boostedJackpotStore } from '../../store/boostedJackpotStore';
import { addKeyboardListener } from '../../core/helpers/keyboard';
import { gameStore } from '../../store/gameStore';
import { spinBarStore } from '../../store/spinBarStore';
import { isMobile } from '../../helpers/isMobile';
import { updateExpiresCount } from '../../helpers/jackpotHelpers';
import { balanceUpdateHandler } from '../../helpers/balanceUpdateHandler';
import { SoundTypes } from '../../services/baseSoundService/soundService';
import { isIChancy } from '../../enums/parnerIds';

// eslint-disable-next-line import/named,no-unused-vars
let wheelAnimation, timeLine;

export class BoostedWheelDrawer {
  constructor() {
    this.popupWinClick = true;
    this.isWinGsapAnimationKillClick = false;
    this.lister = null;
    this.isSendSpin = true;
    this.sectors = null;
    this.wheelValues = null;
    this.radius = 300;
    this.colors = ['#3855E5', '#ffffff'];
    this.gradientColors = [
      { offset: 0, color: '#BB9336' },
      { offset: 0.05, color: '#CBAB5A' },
      { offset: 0.16, color: '#E7D69B' },
      { offset: 0.24, color: '#F8F2C4' },
      { offset: 0.27, color: '#FFFCF3' },
      { offset: 0.32, color: '#FDF9CF' },
      { offset: 0.35, color: '#F5EFC4' },
      { offset: 0.39, color: '#E9DFB2' },
    ];
    this.animations = {};
    this.BoostedWheelContainer = PixiBase.createContainer(containerNames.BoostedWheelContainer);
    this.BoostedWheelContainerView = PixiBase.createContainer(containerNames.BoostedWheelContainer);
    this.BoostedWheelContainer.addChild(this.BoostedWheelContainerView);
    this.BoostedWheelContainer.visible = false;
    this.BoostedWheelContainerView.visible = false;
    this.BoostedWheelContainerView.scale.set(0);
    PixiBase.setPosition(this.BoostedWheelContainer, {
      x: isMobile() ? 269 : 720,
      y: isMobile() ? 480 : 405,
    });
  }

  handleWheelScaleDefault() {
    gsap.to(this.BoostedWheelContainerView.scale, {
      x: isMobile() ? 0.9 : 1,
      y: isMobile() ? 0.9 : 1,
      duration: 1,
    });
  }

  startBoostedWheelContainerGsapAnimation() {
    soundService.play(SoundTypes.OpenWheel);
    PixiBase.setupButtonMode(this.wheelSpinIcon, {
      interactive: true,
      buttonMode: true,
    });
    this.BoostedWheelContainerView.visible = true;
    this.BoostedWheelContainer.visible = true;
    this.handleWheelScaleDefault();
    if (isIChancy()) {
      this.setupIChancyLightningAnim();
    }
    this.initSectorAnimation();
  }

  setupIChancyLightning() {
    if (!isIChancy()) return;
    this.lightning = new Spine({ key: 'lightning' });
    this.lightning.play('idle', true);
    this.isSendSpin = false;
    this.BoostedWheelContainerView.addChild(this.lightning);
  }

  setupIChancyLightningAnim() {
    setTimeout(() => {
      this.lightning.play('win', true);
      this.wheelSpinIcon.play('win', true);
      this.lightning.addListener(
        'scale',
        () => {
          gsap.to(this.BoostedWheelContainerView.scale, {
            x: isMobile() ? 1 : 1.04,
            y: isMobile() ? 1 : 1.04,
            duration: 0.2,
            onComplete: () => {
              this.handleWheelScaleDefault();
            },
          });
        },
        null,
        'win',
        true
      );
      this.lightning.addListener(
        'complete',
        () => {
          this.wheelSpinIcon.play('idle', true);
          this.lightning.play('idle', true);
          this.isSendSpin = true;
        },
        null,
        'win',
        true
      );
    }, 1500);
  }

  setupWheelLamp() {
    const wheelLamp = new Spine({ key: 'wheelLamp' });
    PixiBase.setPosition(wheelLamp, {
      x: isMobile() ? 0 : 0,
      y: isMobile() ? -5 : 0,
    });

    wheelLamp.play('loop2', true);
    this.BoostedWheelContainerView.addChild(wheelLamp);
  }

  setupWheelBlueBoard() {
    const wheelBlueBoard = new Spine({ key: 'wheelBlueBoard' });
    wheelBlueBoard.play('loop1', true);
    this.BoostedWheelContainerView.addChild(wheelBlueBoard);
  }

  setupWheelBorder() {
    const wheelBorder = PixiBase.createSprite(this.spriteSheet.wheelBorder);
    wheelBorder.width = 550;
    wheelBorder.height = 550;
    wheelBorder.anchor.set(0.5);
    this.BoostedWheelContainerView.addChild(wheelBorder);
  }

  setupCursor() {
    const cursor = PixiBase.createSprite(this.spriteSheet.cursor);
    PixiBase.setPosition(cursor, { x: 0, y: -255 });
    cursor.anchor.set(0.5);
    this.BoostedWheelContainerView.addChild(cursor);
  }

  setupWheelSpinIcon() {
    this.wheelSpinIcon = new Spine({ key: 'wheelSpinIcon' });
    this.wheelSpinIcon.play('idle', true);
    this.BoostedWheelContainerView.addChild(this.wheelSpinIcon);
    PixiBase.setupButtonMode(this.wheelSpinIcon, {
      interactive: true,
      buttonMode: true,
    });
    PixiBase.registerPointerup(this.wheelSpinIcon, (e) => {
      if (this.isSendSpin) {
        this.spinToStopSector(e);
      }
    });
  }

  createDrawWheel() {
    const createCanvas = document.createElement('canvas');
    createCanvas.width = 570;
    createCanvas.height = 570;
    const canvas = createCanvas;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const center = { x: canvas.width / 2, y: canvas.height / 2 };
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const sectorAngle = (2 * Math.PI) / this.sectors;

    for (let i = 0; i < this.sectors; i += 1) {
      const startAngle = i * sectorAngle;
      const endAngle = (i + 1) * sectorAngle;

      // Draw sector
      ctx.beginPath();
      ctx.moveTo(center.x, center.y);
      ctx.arc(center.x, center.y, this.radius - 20, startAngle, endAngle);
      ctx.fillStyle = this.colors[i % 2];
      ctx.fill();
      ctx.closePath();

      // Add gradient stroke on each side
      const createGradient = (angle) => {
        const gradient = ctx.createLinearGradient(
          center.x + Math.cos(angle) * (this.radius - 20),
          center.y + Math.sin(angle) * (this.radius - 20),
          center.x,
          center.y
        );
        this.gradientColors.forEach(({ offset, color }) => gradient.addColorStop(offset, color));
        return gradient;
      };

      ctx.strokeStyle = createGradient(startAngle);
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(
        center.x + Math.cos(startAngle) * (this.radius - 20),
        center.y + Math.sin(startAngle) * (this.radius - 20)
      );
      ctx.lineTo(center.x, center.y);
      ctx.stroke();

      ctx.strokeStyle = createGradient(endAngle);
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(
        center.x + Math.cos(endAngle) * (this.radius - 20),
        center.y + Math.sin(endAngle) * (this.radius - 20)
      );
      ctx.lineTo(center.x, center.y);
      ctx.stroke();

      // Add text to sector
      ctx.save();
      ctx.translate(center.x, center.y);
      ctx.rotate((startAngle + endAngle) / 2);
      ctx.textBaseline = 'middle'; // Ensures the text is centered vertically
      ctx.textAlign = 'right';
      ctx.fillStyle = i % 2 === 0 ? '#FFC33E' : '#0F41CE';
      ctx.font = 'bold 22px Verdana';
      ctx.fillText(isoStandard(this.wheelValues[i]), 240, 4);
      ctx.restore();
    }
    const dataURL = canvas.toDataURL('image/png');
    this.wheelSector = PixiBase.createSprite(PixiBase.PIXI.Texture.from(dataURL));
    this.wheelSector.width = 500;
    this.wheelSector.height = 500;
    this.wheelSector.anchor.set(0.5);
    this.BoostedWheelContainerView.addChild(this.wheelSector);
  }

  setupWheelWinPopup() {
    this.winPopup = new Spine({ key: 'popup' });
    this.winPopup.visible = false;
    const textYouHave = PixiBase.createText(gameStore.canvasTranslationKeys.YOU_HAVE_WON, {
      fontSize: 48,
      fontWeight: 700,
      fontFamily: 'Poppins',
      fill: '#FF9000',
      align: 'center',
    });
    textYouHave.anchor.set(0.5);
    this.textWinAmount = PixiBase.createText('', {
      fontSize: 48,
      fontWeight: 700,
      fontFamily: 'Poppins',
      fill: '#0F40CE',
      align: 'center',
    });
    this.textWinAmount.anchor.set(0.5);
    this.winPopup.addChildToSlot('winPopupText', textYouHave);
    this.winPopup.addChildToSlot('winPopupNumbers', this.textWinAmount);
    this.BoostedWheelContainerView.addChild(this.winPopup);
    PixiBase.setupButtonMode(this.winPopup, {
      interactive: true,
      buttonMode: true,
    });
    PixiBase.registerPointerup(this.winPopup, () => {
      this.endAnimationWinPopup();
    });
  }

  startGsapWinAnimation(textWinAmount) {
    const { spinWheelWinAmount } = boostedJackpotStore;
    textWinAmount.text = '';
    textWinAmount.textContent = '';
    timeLine && timeLine.kill();
    timeLine = gsap.timeline();
    timeLine.to(textWinAmount, {
      textContent: spinWheelWinAmount,
      duration: 3,
      ease: 'none',
      snap: { textContent: 0.01 },
      stagger: {
        each: 1,
        onUpdate: function () {
          // eslint-disable-next-line react/no-this-in-sfc
          textWinAmount.text = isoStandard(this.targets()[0].textContent, true);
        },
        onComplete: function () {
          timeLine && timeLine.kill();
          textWinAmount.text = isoStandard(spinWheelWinAmount);
        },
      },
    });
  }

  startWinPopup() {
    this.winPopup.stop();
    this.startGsapWinAnimation(this.textWinAmount);
    soundService.play(SoundTypes.WheelWinPopup);
    this.winPopup.play('start', false);
    this.winPopup.addListener(
      'complete',
      () => {
        this.lister = addKeyboardListener(['Enter', ' '], () => {
          this.endAnimationWinPopup();
        });
        this.winPopup.play('loop', true);
      },
      null,
      'start'
    );
  }

  // eslint-disable-next-line consistent-return
  endAnimationWinPopup() {
    if (!this.popupWinClick) {
      return null;
    }
    const { spinWheelWinAmount } = boostedJackpotStore;
    timeLine && timeLine.kill();
    this.textWinAmount.text = isoStandard(spinWheelWinAmount);
    if (this.isWinGsapAnimationKillClick) {
      this.popupWinClick = false;
      this.winPopup.play('end', false);
      this.winPopup.addListener(
        'complete',
        () => {
          soundService.stop(SoundTypes.WheelWinPopup);
          this.isWinGsapAnimationKillClick = false;
          this.BoostedWheelContainerView.visible = false;
          this.BoostedWheelContainer.visible = false;
          this.BoostedWheelContainerView.scale.set(0);
          this.winPopup.visible = false;
          this.popupWinClick = true;
          gameStore.setBonusType(null);
          spinBarStore.setIsSpinStart(false);
          this.lister && this.lister();
          balanceUpdateHandler();
        },
        null,
        'end'
      );
    } else {
      this.isWinGsapAnimationKillClick = true;
    }
  }

  spinToSectorLoopAnimation() {
    const targetAngle = 360 * 5;
    // soundService.play(SoundTypes.wheelSpinning);
    wheelAnimation = gsap.fromTo(
      this.wheelSector,
      { rotation: 0 },
      {
        rotation: targetAngle * (Math.PI / 180),
        duration: 3,
        repeat: -1,
        ease: 'none',
      }
    );
  }

  initSectorAnimation() {
    const sectorAngle = 360 / this.sectors;
    const targetAngle = 360 * 5 - sectorAngle / 2;
    gsap.to(this.wheelSector, {
      rotation: targetAngle * (Math.PI / 180),
      duration: 0,
      ease: 'linear',
    });
  }

  spinToSectorEndAnimation(sectorStopIndex) {
    if (wheelAnimation) {
      wheelAnimation.kill();
      const sectorAngle = 360 / this.sectors;
      const arrowAngle = 270;
      const targetAngle =
        sectorStopIndex === null
          ? 360 * 5 - (sectorStopIndex * sectorAngle + sectorAngle / 2)
          : 360 * 5 + arrowAngle - (sectorStopIndex * sectorAngle + sectorAngle / 2);
      // const targetAngle = 360 * 5 + 270 - (sectorStopIndex * sectorAngle + sectorAngle / 2);
      gsap.to(this.wheelSector, {
        rotation: targetAngle * (Math.PI / 180),
        duration: 3,
        ease: 'power1.out',
        onComplete: () => {
          this.winPopup.visible = true;
          this.startWinPopup();
        },
      });
    }
  }

  spinToStopSector(e) {
    soundService.play(SoundTypes.SpinWheelButton);
    gameService.getBoostedJackpot();
    this.spinToSectorLoopAnimation();
    if (e) {
      e.target.interactive = false;
    }
  }

  createGraphicsForBoostedJackpot() {
    const graphics = PixiBase.createGraphics();
    this.graphics = graphics;
    graphics.beginFill('black');
    graphics.alpha = 0.7;
    graphics.interactive = true;
    graphics.drawRect(
      isMobile() ? -270 : -720,
      isMobile() ? -480 : -405,
      isMobile() ? 540 : 1440,
      isMobile() ? 960 : 810
    );
    graphics.endFill();
    this.BoostedWheelContainer.addChildAt(this.graphics, 0);
  }

  executeWheelDataAndWheel(wheelSectors, wheelValues) {
    this.sectors = wheelSectors;
    this.wheelValues = wheelValues;
    this.createGraphicsForBoostedJackpot();
    this.setupWheelBlueBoard();
    this.createDrawWheel();
    this.setupWheelBorder();
    this.setupWheelLamp();
    this.setupIChancyLightning();
    this.setupWheelSpinIcon();
    if (!isIChancy()) {
      this.setupCursor();
    }
    this.setupWheelWinPopup();
  }

  execute(config, spriteSheet) {
    const [gameImageSpriteSheet] = spriteSheet;
    this.config = config;
    this.spriteSheet = gameImageSpriteSheet;
  }
}
